import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Paper,
  CircularProgress,
  Popper,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  ClickAwayListener,
  Collapse,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import ReplyIcon from '@mui/icons-material/Reply';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { messagingApi, ChatMessage, MessagingUser } from '../api/messaging';

interface MessagingThreadProps {
  conversationId: number;
  conversationName: string;
  conversationType?: 'DIRECT' | 'GROUP';
  onMessageSent?: () => void;
}

function renderMessageText(text: string, isMine = false): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  const regex = /@\[([^\]]+)\]\(user_id:\d+\)/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(text.substring(lastIdx, match.index));
    }
    parts.push(
      <Typography
        key={match.index}
        component="span"
        sx={{
          color: isMine ? 'rgba(255,255,255,0.85)' : 'primary.main',
          fontWeight: 700,
          fontSize: 'inherit',
          bgcolor: isMine ? 'rgba(255,255,255,0.18)' : 'rgba(25,118,210,0.08)',
          borderRadius: 0.5,
          px: 0.4,
        }}
      >
        @{match[1]}
      </Typography>
    );
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) {
    parts.push(text.substring(lastIdx));
  }
  return parts.length > 0 ? <>{parts}</> : text;
}

function mentionToDisplay(text: string): string {
  return text.replace(/@\[([^\]]+)\]\(user_id:\d+\)/g, '@$1');
}

function getCurrentUserId(): number | null {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u?.id ?? null;
  } catch {
    return null;
  }
}

export default function MessagingThread({ conversationId, conversationName, conversationType, onMessageSent }: MessagingThreadProps) {
  const currentUserId = getCurrentUserId();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [users, setUsers] = useState<MessagingUser[]>([]);
  const [groupMembers, setGroupMembers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const [displayText, setDisplayText] = useState('');
  const [mentionMap, setMentionMap] = useState<Map<string, { name: string; id: number }>>(new Map());

  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);

  const [mentionAnchor, setMentionAnchor] = useState<HTMLElement | null>(null);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);

  const [pinnedBannerIndex, setPinnedBannerIndex] = useState(0);
  const messageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchMessages = useCallback(async () => {
    try {
      const data = await messagingApi.getMessages(conversationId);
      setMessages(data || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await messagingApi.getUsers();
      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setGroupMembers([]);
    fetchMessages();
    fetchUsers();
  }, [fetchMessages, fetchUsers]);

  // Fetch group member names for GROUP conversations
  useEffect(() => {
    if (conversationType === 'GROUP') {
      messagingApi.getConversation(conversationId).then(data => {
        setGroupMembers(data.members.map(m => m.user.name));
      }).catch(() => {});
    }
  }, [conversationId, conversationType]);

  // Mark conversation as read when opened
  useEffect(() => {
    messagingApi.markRead(conversationId).catch(() => {});
  }, [conversationId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const buildRawText = (display: string, mentions: Map<string, { name: string; id: number }>): string => {
    let raw = display;
    const sortedMentions = Array.from(mentions.entries()).sort((a, b) => b[0].length - a[0].length);
    for (const [displayName, { name, id }] of sortedMentions) {
      raw = raw.split(`@${displayName}`).join(`@[${name}](user_id:${id})`);
    }
    return raw;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDisplayText(value);

    const cursorPos = e.target.selectionStart || 0;
    const textUpToCursor = value.substring(0, cursorPos);
    const lastAtIndex = textUpToCursor.lastIndexOf('@');

    if (lastAtIndex >= 0) {
      const textAfterAt = textUpToCursor.substring(lastAtIndex + 1);
      const charBeforeAt = lastAtIndex > 0 ? value[lastAtIndex - 1] : ' ';
      if ((charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0) && !textAfterAt.includes(' ')) {
        setMentionSearch(textAfterAt.toLowerCase());
        setMentionStart(lastAtIndex);
        setMentionAnchor(inputRef.current);
        setMentionHighlightIndex(0);
        return;
      }
    }
    setMentionAnchor(null);
    setMentionSearch('');
    setMentionHighlightIndex(0);
  };

  const handleMentionSelect = (user: MessagingUser) => {
    const before = displayText.substring(0, mentionStart);
    const cursorPos = mentionStart + mentionSearch.length + 1;
    const after = displayText.substring(cursorPos);
    const newDisplay = `${before}@${user.name} ${after}`;
    setDisplayText(newDisplay);

    setMentionMap(prev => {
      const next = new Map(prev);
      next.set(user.name, { name: user.name, id: user.id });
      return next;
    });

    setMentionAnchor(null);
    setMentionSearch('');
    setMentionHighlightIndex(0);
    inputRef.current?.focus();
  };

  const handleSend = async () => {
    if (!displayText.trim() || sending) return;
    const rawText = buildRawText(displayText.trim(), mentionMap);
    const parentId = replyTo?.id;

    const tempMsg: ChatMessage = {
      id: Date.now(),
      conversation_id: conversationId,
      content: rawText,
      is_pinned: false,
      created_by_id: currentUserId || 0,
      parent_id: parentId || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      author: { id: currentUserId || 0, name: 'You', email: '' },
    };
    setMessages(prev => [...prev, tempMsg]);
    setDisplayText('');
    setMentionMap(new Map());
    setReplyTo(null);

    try {
      setSending(true);
      await messagingApi.sendMessage(conversationId, { content: rawText, parent_id: parentId });
      await fetchMessages();
      await messagingApi.markRead(conversationId).catch(() => {});
      onMessageSent?.();
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      setDisplayText(mentionToDisplay(rawText));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mentionAnchor && filteredMentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionHighlightIndex(prev => (prev + 1) % Math.min(filteredMentionUsers.length, 8));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionHighlightIndex(prev => (prev - 1 + Math.min(filteredMentionUsers.length, 8)) % Math.min(filteredMentionUsers.length, 8));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleMentionSelect(filteredMentionUsers[mentionHighlightIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionAnchor(null);
        setMentionSearch('');
        setMentionHighlightIndex(0);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTogglePin = async (msgId: number, newPinnedState: boolean) => {
    setMessages(prev =>
      prev.map(m => (m.id === msgId ? { ...m, is_pinned: newPinnedState } : m))
    );
    try {
      await messagingApi.pinMessage(msgId, newPinnedState);
    } catch (err) {
      console.error('Error toggling pin:', err);
      setMessages(prev =>
        prev.map(m => (m.id === msgId ? { ...m, is_pinned: !newPinnedState } : m))
      );
    }
  };

  const scrollToMessage = (msgId: number) => {
    const el = messageRefs.current.get(msgId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const filteredMentionUsers = users.filter(u =>
    u.name.toLowerCase().includes(mentionSearch) || u.email.toLowerCase().includes(mentionSearch)
  );

  const topLevel = messages.filter(m => !m.parent_id);
  const repliesMap = new Map<number, ChatMessage[]>();
  messages.filter(m => m.parent_id).forEach(m => {
    const arr = repliesMap.get(m.parent_id!) || [];
    arr.push(m);
    repliesMap.set(m.parent_id!, arr);
  });

  const pinnedMessages = messages.filter(m => m.is_pinned);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pinnedMessages.length === 0) setPinnedBannerIndex(0);
    else if (pinnedBannerIndex >= pinnedMessages.length)
      setPinnedBannerIndex(pinnedMessages.length - 1);
  }, [pinnedMessages.length]);

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const renderMessage = (msg: ChatMessage, isReply = false) => {
    const isMine =
      (currentUserId !== null && msg.author?.id === currentUserId) ||
      (currentUserId !== null && msg.created_by_id === currentUserId) ||
      msg.author?.name === 'You';

    const authorName = msg.author?.name || '?';

    return (
      <Box
        key={msg.id}
        ref={(el: HTMLDivElement | null) => {
          if (el) messageRefs.current.set(msg.id, el);
          else messageRefs.current.delete(msg.id);
        }}
        sx={{
          display: 'flex',
          flexDirection: isMine ? 'row-reverse' : 'row',
          gap: 1,
          mb: 1.5,
          ml: (!isMine && isReply) ? { xs: 4, sm: 5 } : 0,
          mr: (isMine && isReply) ? { xs: 4, sm: 5 } : 0,
          alignItems: 'flex-end',
          '&:hover .msg-actions': { opacity: 1 },
          ...(msg.is_pinned && {
            borderLeft: '3px solid',
            borderLeftColor: 'warning.main',
            pl: 0.5,
            bgcolor: alpha('#f59e0b', 0.06),
            borderRadius: 1,
          })
        }}
      >
        {!isMine && (
          <Avatar sx={{
            width: isReply ? 24 : 30,
            height: isReply ? 24 : 30,
            fontSize: isReply ? '0.65rem' : '0.75rem',
            bgcolor: isReply ? 'grey.400' : 'secondary.main',
            flexShrink: 0,
            mb: 0.3
          }}>
            {authorName[0].toUpperCase()}
          </Avatar>
        )}

        <Box sx={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start' }}>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Paper
              elevation={0}
              sx={{
                px: 1.5, py: 1,
                borderRadius: 2.5,
                ...(isMine
                  ? { borderBottomRightRadius: 4, bgcolor: 'primary.main' }
                  : { borderBottomLeftRadius: 4, bgcolor: isReply ? alpha('#9e9e9e', 0.12) : alpha('#1976d2', 0.07) }
                ),
                display: 'inline-block',
              }}
            >
              {!isMine && (
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color={isReply ? 'text.secondary' : 'secondary.main'}
                  sx={{ fontSize: '0.72rem', display: 'block', mb: 0.2 }}
                >
                  {authorName}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  wordBreak: 'break-word',
                  lineHeight: 1.5,
                  fontSize: '0.84rem',
                  color: isMine ? 'primary.contrastText' : 'text.primary'
                }}
              >
                {renderMessageText(msg.content, isMine)}
              </Typography>
            </Paper>
            {msg.is_pinned && (
              <PushPinIcon
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  fontSize: 14,
                  color: 'warning.main',
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  p: 0.2,
                  boxShadow: 1,
                }}
              />
            )}
          </Box>

          <Box
            className="msg-actions"
            sx={{
              display: 'flex',
              flexDirection: isMine ? 'row-reverse' : 'row',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.3,
              opacity: 0,
              transition: 'opacity 0.15s'
            }}
          >
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', px: 0.5 }}>
              {formatTime(msg.created_at)}
            </Typography>
            <Tooltip title={msg.is_pinned ? 'Unpin' : 'Pin message'} placement="top">
              <IconButton
                size="small"
                onClick={() => handleTogglePin(msg.id, !msg.is_pinned)}
                sx={{ p: 0.3, color: msg.is_pinned ? 'warning.main' : 'text.disabled' }}
              >
                {msg.is_pinned
                  ? <PushPinIcon sx={{ fontSize: 18 }} />
                  : <PushPinOutlinedIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            {!isReply && (
              <IconButton size="small" onClick={() => { setReplyTo(msg); inputRef.current?.focus(); }} sx={{ p: 0.3 }}>
                <ReplyIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const INPUT_BAR_HEIGHT = replyTo ? 110 : 66;

  return (
    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0, bgcolor: 'background.paper', zIndex: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {conversationName}
        </Typography>
        {conversationType === 'GROUP' && groupMembers.length > 0 ? (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: '100%' }}>
            {groupMembers.join(', ')}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {messages.length} message{messages.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {/* Pinned Message Banner */}
      <Collapse in={pinnedMessages.length > 0} unmountOnExit>
        <Box sx={{
          px: 1.5, py: 0.75,
          bgcolor: alpha('#f59e0b', 0.12),
          borderBottom: '1px solid',
          borderBottomColor: alpha('#f59e0b', 0.3),
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          minHeight: 40,
        }}>
          <PushPinIcon sx={{ fontSize: 14, color: 'warning.dark', flexShrink: 0 }} />

          <Box
            sx={{ flex: 1, overflow: 'hidden', cursor: 'pointer' }}
            onClick={() => { const p = pinnedMessages[pinnedBannerIndex]; if (p) scrollToMessage(p.id); }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', color: 'warning.dark', display: 'block', lineHeight: 1.2 }}>
              Pinned Message
              {pinnedMessages.length > 1 && (
                <Typography component="span" variant="caption" sx={{ fontWeight: 400, color: 'text.secondary', ml: 0.75, fontSize: '0.62rem' }}>
                  {pinnedBannerIndex + 1} of {pinnedMessages.length}
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" noWrap sx={{ fontSize: '0.75rem', color: 'text.primary', display: 'block' }}>
              {pinnedMessages[pinnedBannerIndex]?.content
                .replace(/@\[([^\]]+)\]\(user_id:\d+\)/g, '@$1')
                .substring(0, 80) ?? ''}
            </Typography>
          </Box>

          {pinnedMessages.length > 1 && (
            <>
              <IconButton size="small" sx={{ p: 0.2 }}
                onClick={() => setPinnedBannerIndex(i => (i - 1 + pinnedMessages.length) % pinnedMessages.length)}>
                <NavigateBeforeIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" sx={{ p: 0.2 }}
                onClick={() => setPinnedBannerIndex(i => (i + 1) % pinnedMessages.length)}>
                <NavigateNextIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          )}

          <Tooltip title="Unpin this message" placement="top">
            <IconButton size="small" sx={{ p: 0.2, color: 'text.secondary' }}
              onClick={() => { const p = pinnedMessages[pinnedBannerIndex]; if (p) handleTogglePin(p.id, false); }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Collapse>

      {/* Messages */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          pb: `${INPUT_BAR_HEIGHT + 8}px`,
          bgcolor: '#fafbfc',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body2" color="text.disabled">No messages yet</Typography>
            <Typography variant="caption" color="text.disabled">Start the conversation!</Typography>
          </Box>
        ) : (
          topLevel.map(msg => (
            <React.Fragment key={msg.id}>
              {renderMessage(msg)}
              {(repliesMap.get(msg.id) || []).map(reply => renderMessage(reply, true))}
            </React.Fragment>
          ))
        )}
      </Box>

      {/* Bottom input */}
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        zIndex: 2
      }}>
        {replyTo && (
          <Box sx={{
            px: 2, py: 0.5, bgcolor: alpha('#1976d2', 0.06),
            display: 'flex', alignItems: 'center', gap: 1,
            borderLeft: '3px solid', borderLeftColor: 'primary.main'
          }}>
            <ReplyIcon sx={{ fontSize: 14, color: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>
              Replying to <strong>{replyTo.author?.name}</strong>
            </Typography>
            <IconButton size="small" onClick={() => setReplyTo(null)} sx={{ p: 0.3 }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}

        <Box sx={{
          px: { xs: 1, sm: 1.5 },
          pt: 0.75,
          pb: { xs: 'max(env(safe-area-inset-bottom, 0px), 12px)', sm: '12px' },
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end'
        }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            placeholder="Type a message... @ to mention"
            value={displayText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            multiline
            maxRows={4}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                fontSize: '0.875rem',
                bgcolor: '#f5f5f5',
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'divider' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1 }
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!displayText.trim() || sending}
            sx={{
              mb: 0.3,
              bgcolor: displayText.trim() ? 'primary.main' : 'transparent',
              color: displayText.trim() ? 'white' : 'text.disabled',
              width: 36, height: 36,
              '&:hover': { bgcolor: displayText.trim() ? 'primary.dark' : 'transparent' },
              '&.Mui-disabled': { bgcolor: 'transparent' }
            }}
          >
            {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Box>

      {/* Mention Autocomplete */}
      <Popper
        open={Boolean(mentionAnchor) && filteredMentionUsers.length > 0}
        anchorEl={mentionAnchor}
        placement="top-start"
        sx={{ zIndex: 1400 }}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      >
        <ClickAwayListener onClickAway={() => setMentionAnchor(null)}>
          <Paper sx={{ maxHeight: 200, overflowY: 'auto', width: 260, boxShadow: 6, borderRadius: 2 }}>
            <List dense sx={{ p: 0.5 }}>
              {filteredMentionUsers.slice(0, 8).map((user, idx) => (
                <ListItemButton
                  key={user.id}
                  onClick={() => handleMentionSelect(user)}
                  selected={idx === mentionHighlightIndex}
                  sx={{
                    py: 0.75,
                    borderRadius: 1,
                    '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                    '&.Mui-selected .MuiTypography-root': { color: 'inherit' },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    <Avatar sx={{
                      width: 26, height: 26, fontSize: '0.7rem',
                      bgcolor: idx === mentionHighlightIndex ? 'rgba(255,255,255,0.3)' : 'secondary.main'
                    }}>{user.name[0]}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={500}>{user.name}</Typography>}
                    secondary={<Typography variant="caption" color={idx === mentionHighlightIndex ? 'rgba(255,255,255,0.75)' : 'text.disabled'} sx={{ fontSize: '0.65rem' }}>{user.email}</Typography>}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
